import { Controller } from '@hotwired/stimulus'
import Swal from 'sweetalert2'
import consumer from '~/channels/consumer'

/**
 * Phase Transition Controller
 *
 * Handles SweetAlert confirmation for website order phase transitions
 * and AJAX submission with UI updates without page reload.
 *
 * Also subscribes to WebsiteOrderChannel for real-time updates when
 * customers approve/request changes on their side.
 */
export default class extends Controller {
  static targets = [
    'form',
    'phaseSelect',
    'notesInput',
    'submitBtn',
    'progressBar',
    'stepIndicator',
    'stepLabel',
    'headerBadge',
    'formBadge',
    'positionText',
    'formFields',
    'noTransitionsAlert',
    'trackerCard'
  ]

  static values = {
    url: String,
    websiteId: String
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', this.handleSubmit.bind(this))
    }

    // Subscribe to real-time updates for customer-initiated changes
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener('submit', this.handleSubmit.bind(this))
    }

    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  subscribeToChannel() {
    if (!this.websiteIdValue) {
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteOrderChannel', website_id: this.websiteIdValue },
      {
        connected: () => {
          console.log('[PhaseTransition] ActionCable connected')
        },
        disconnected: () => {
          console.log('[PhaseTransition] ActionCable disconnected')
        },
        received: (data) => {
          console.log('[PhaseTransition] Received remote update:', data)
          this.handleRemoteUpdate(data)
        }
      }
    )
  }

  handleRemoteUpdate(data) {
    if (data.event === 'phase_changed' && data.actor_type === 'user') {
      // Customer made a change - show notification and reload
      this.showRemoteUpdateNotification(data)
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
  }

  showRemoteUpdateNotification(data) {
    Swal.fire({
      title: 'Order Updated by Customer',
      html: `
        <p class="text-gray-600">The customer has updated their order.</p>
        <p class="text-lg font-semibold text-gray-900 mt-2">New status: ${data.phase_name}</p>
        <p class="text-sm text-gray-500 mt-2">Page will refresh to show changes...</p>
      `,
      icon: 'info',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true
    })
  }

  async handleSubmit(event) {
    event.preventDefault()

    const selectedPhase = this.hasPhaseSelectTarget ? this.phaseSelectTarget.value : null
    const selectedPhaseName = this.hasPhaseSelectTarget ?
      this.phaseSelectTarget.options[this.phaseSelectTarget.selectedIndex]?.text :
      'Unknown'

    if (!selectedPhase) {
      await Swal.fire({
        title: 'No Phase Selected',
        text: 'Please select a phase to transition to.',
        icon: 'warning',
        confirmButtonColor: '#3b82f6',
        confirmButtonText: 'OK'
      })
      return
    }

    const result = await Swal.fire({
      title: 'Transition Phase?',
      html: `
        <p class="text-gray-600 mb-3">You are about to transition this order to:</p>
        <p class="text-lg font-semibold text-gray-900">${selectedPhaseName}</p>
        <p class="text-sm text-gray-500 mt-3">This action will be recorded in the activity history.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Transition',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    })

    if (result.isConfirmed) {
      await this.submitTransition(selectedPhase)
    }
  }

  async submitTransition(selectedPhase) {
    const notes = this.hasNotesInputTarget ? this.notesInputTarget.value : ''

    // Show loading state
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.innerHTML = `
        <svg class="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Transitioning...
      `
    }

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          to_phase: selectedPhase,
          notes: notes
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.updateUI(data)
        await Swal.fire({
          title: 'Phase Updated',
          text: data.message,
          icon: 'success',
          confirmButtonColor: '#3b82f6',
          timer: 1500,
          timerProgressBar: true
        })
        // Reload page to show phase-specific content panels
        window.location.reload()
      } else {
        throw new Error(data.error || 'Failed to transition phase')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Transition error:', error)
      await Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to transition phase. Please try again.',
        icon: 'error',
        confirmButtonColor: '#3b82f6'
      })
    } finally {
      // Reset button state
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.innerHTML = 'Transition Phase'
      }
    }
  }

  updateUI(data) {
    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress_percentage}%`
    }

    // Update step indicators
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTargets.forEach((indicator) => {
        const phase = indicator.dataset.phase
        const status = data.phase_statuses.find((s) => s.phase === phase)
        if (!status) return

        const checkIcon = indicator.querySelector('.step-check')
        const stepNumber = indicator.querySelector('.step-number')

        // Reset classes
        indicator.className = 'w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors phase-step-indicator'

        if (status.completed) {
          indicator.classList.add('bg-green-500', 'text-white')
          indicator.style = ''
          if (checkIcon) checkIcon.classList.remove('hidden')
          if (stepNumber) stepNumber.classList.add('hidden')
        } else if (status.current) {
          indicator.style = 'background: var(--text-primary); color: white; box-shadow: 0 0 0 4px var(--bg-surface), 0 0 0 6px var(--text-primary);'
          if (checkIcon) checkIcon.classList.add('hidden')
          if (stepNumber) stepNumber.classList.remove('hidden')
        } else {
          indicator.style = 'background: var(--bg-muted); color: var(--text-muted);'
          if (checkIcon) checkIcon.classList.add('hidden')
          if (stepNumber) stepNumber.classList.remove('hidden')
        }
      })
    }

    // Update step labels
    if (this.hasStepLabelTarget) {
      this.stepLabelTargets.forEach((label) => {
        const phase = label.dataset.phase
        const status = data.phase_statuses.find((s) => s.phase === phase)
        if (!status) return

        // Reset styling
        label.classList.remove('font-semibold')

        if (status.current) {
          label.classList.add('font-semibold')
          label.style.color = 'var(--text-primary)'
        } else {
          label.style.color = 'var(--text-tertiary)'
        }
      })
    }

    // Update badges
    const badgeClasses = ['badge-default', 'badge-info', 'badge-warning', 'badge-secondary', 'badge-success']

    if (this.hasHeaderBadgeTarget) {
      badgeClasses.forEach((c) => this.headerBadgeTarget.classList.remove(c))
      this.headerBadgeTarget.classList.add(data.badge_class)
      this.headerBadgeTarget.textContent = data.phase_name
    }

    if (this.hasFormBadgeTarget) {
      badgeClasses.forEach((c) => this.formBadgeTarget.classList.remove(c))
      this.formBadgeTarget.classList.add(data.badge_class)
      this.formBadgeTarget.textContent = data.phase_name
    }

    // Update position text
    if (this.hasPositionTextTarget) {
      this.positionTextTarget.textContent = `Position ${data.phase_position} of ${data.total_phases}`
    }

    // Update phase select options
    if (this.hasPhaseSelectTarget && this.hasFormFieldsTarget) {
      if (data.allowed_transitions.length > 0) {
        // Rebuild select options
        this.phaseSelectTarget.innerHTML = '<option value="">Select phase...</option>'
        data.allowed_transitions.forEach((option) => {
          const optEl = document.createElement('option')
          optEl.value = option.value
          optEl.textContent = option.label
          this.phaseSelectTarget.appendChild(optEl)
        })

        // Clear notes
        if (this.hasNotesInputTarget) {
          this.notesInputTarget.value = ''
        }

        // Show form fields, hide no transitions alert
        this.formFieldsTarget.querySelector('.grid')?.classList.remove('hidden')
        if (this.hasSubmitBtnTarget) this.submitBtnTarget.classList.remove('hidden')
        if (this.hasNoTransitionsAlertTarget) this.noTransitionsAlertTarget.classList.add('hidden')
      } else {
        // No transitions available - show alert, hide form
        this.formFieldsTarget.innerHTML = `
          <div class="alert alert-info" data-admin--phase-transition-target="noTransitionsAlert">
            <div class="alert-icon">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="alert-content">
              <p class="alert-description">No transitions available from the current phase.</p>
            </div>
          </div>
        `
      }
    }
  }
}
